import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '../components/Admin/Reusable/Breadcrumb';
import { useTranslation } from 'react-i18next';
import { Card, Col, Container, Row } from 'react-bootstrap';

import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { Button, Chip, TextField } from '@mui/material';
import CustomModal from '../components/Common/Modal/CustomModal';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { formatDateFull, formatOnlyDate, formattedPrice } from '../utils';
import FormTitleMain from '../components/Admin/Reusable/FormTitleMain';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { fetchReceiptById } from '../actions/user';

const formatSizeLabel = (size) => {
  if (!size) return '';

  const [unit, value] = size.split('_');

  if (unit === 'KG') return `${value}kg`;
  if (unit === 'GR') return `${value}gr`;

  return size;
};

const coffeeTypeMap = {
  WHOLE_BEANS: 'Whole beans',
  ESPRESSO_MACHINE: 'Espresso Machine',
  MOKA_POT: 'Moka Pot',
  AEROPRESS: 'Aeropress',
  V60: 'V60',
  FRENCH_PRESS: 'French Press',
  CHEMEX: 'Chemex',
  AUTOMATIC_FILTER_MACHINE: 'Automatic Filter Machine',
  TURKISH: 'Turkish',
};

const formatCoffeeType = (type) => {
  return coffeeTypeMap[type] || type;
};

const fryingMap = {
  ESPRESSO: 'Espreso',
  FILTERED: 'Filter',
};

const formatFryingType = (type) => {
  return fryingMap[type] || type;
};

const ReceiptDetail = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { receiptDetail } = useSelector((state) => state.userReducer);

  //breadcrumb
  const translateModule = 'receipt';
  const pageType = id ? 'Edit' : 'Add';
  const breadcrumbItems = [
    { label: t(`breadcrumb.${translateModule}`) },
    {
      label: t(`breadcrumb.${translateModule}${pageType}`),
      active: true,
    },
  ];

  useEffect(() => {
    dispatch(fetchReceiptById(id));
  }, [dispatch, id]);

  const statusName =
    receiptDetail?.status == 'PENDING' ? (
      <Chip
        label="Čeka plaćanje"
        sx={{
          backgroundColor: '#FFE0B2', // Nova pastelna narandžasta
          color: '#EF6C00', // Tamnija narandžasta za tekst
        }}
        size="small"
      />
    ) : receiptDetail?.status == 'PAID' ? (
      <Chip
        label="Plaćeno"
        sx={{
          backgroundColor: '#E8F5E9', // Blaga zelena
          color: '#2E7D32', // Tamnija zelena za tekst
        }}
        size="small"
      />
    ) : receiptDetail?.status == 'DECLINE' ? (
      <Chip
        label="Odbijeno"
        sx={{
          backgroundColor: '#FFEBEE', // Blaga crvena
          color: '#C62828', // Tamnija crvena za tekst
        }}
        size="small"
      />
    ) : receiptDetail?.status == 'ON_DELIVERY' ? (
      <Chip
        label="Pouzećem"
        sx={{
          backgroundColor: '#E8F5E9', // Blaga zelena
          color: '#2E7D32', // Tamnija zelena za tekst
        }}
        size="small"
      />
    ) : (
      <Chip label="Nepoznat" size="small" />
    );

  const statusNameDelivery =
    receiptDetail?.deliverStatus == 'PENDING' ? (
      <Chip
        label="Nije isporučeno"
        sx={{
          backgroundColor: '#FFEBEE', // Blaga crvena
          color: '#C62828', // Tamnija crvena za tekst
        }}
        size="small"
      />
    ) : (
      <Chip
        label="Isporučeno"
        sx={{
          backgroundColor: '#E8F5E9', // Blaga zelena
          color: '#2E7D32', // Tamnija zelena za tekst
        }}
        size="small"
      />
    );

  // izračunavanje ukupne sume svih item-a
  const totalItemsPrice = receiptDetail?.items?.reduce(
    (sum, item) => sum + (parseFloat(item?.price) || 0),
    0
  );
  const itemType = {
    COURSE: 'KURS',
  };

  return (
    <Fragment>
      <Breadcrumb
        breadcrumbItems={breadcrumbItems}
        type={`${translateModule}${pageType}`}
        id={id}
        extraTitle={receiptDetail?.receiptNumber}
        pageType={pageType}
      />
      <Container>
        <Row>
          <Col lg={12}>
            <Row>
              <Col lg={12}>
                <div className="receipt-detail">
                  <div className="receipt-detail__header">
                    <div className="receipt-detail__title">Osnovni podaci</div>

                    {statusName}
                    {statusNameDelivery}
                  </div>

                  <div className="receipt-detail__grid">
                    <div className="receipt-detail__row">
                      <span className="receipt-detail__label">
                        {t('receiptDetail.originalReceiptNumber')}
                      </span>
                      <span className="receipt-detail__value receipt-detail__mono">
                        {receiptDetail?.receiptNumber}
                      </span>
                    </div>
                    <div className="receipt-detail__row">
                      <span className="receipt-detail__label">Ime i prezime</span>
                      <span className="receipt-detail__value">
                        {receiptDetail?.name} {receiptDetail?.surname}
                      </span>
                    </div>
                    <div className="receipt-detail__row">
                      <span className="receipt-detail__label">Email</span>
                      <span className="receipt-detail__value">{receiptDetail?.email}</span>
                    </div>
                    <div className="receipt-detail__row">
                      <span className="receipt-detail__label">Grad</span>
                      <span className="receipt-detail__value">{receiptDetail?.city}</span>
                    </div>
                    <div className="receipt-detail__row">
                      <span className="receipt-detail__label">Adresa</span>
                      <span className="receipt-detail__value">
                        {receiptDetail?.address}, {receiptDetail?.apartmentNumber},{' '}
                        {receiptDetail?.postalCode}
                      </span>
                    </div>
                    <div className="receipt-detail__row">
                      <span className="receipt-detail__label">Kompanija</span>
                      <span className="receipt-detail__value">{receiptDetail?.companyName}</span>
                    </div>
                    <div className="receipt-detail__row">
                      <span className="receipt-detail__label">Telefon</span>
                      <span className="receipt-detail__value">{receiptDetail?.phone}</span>
                    </div>

                    <div className="receipt-detail__row">
                      <span className="receipt-detail__label">Iznos</span>
                      <span className="receipt-detail__value">
                        {formattedPrice(receiptDetail?.totalPrice)}
                      </span>
                    </div>

                    <div className="receipt-detail__row">
                      <span className="receipt-detail__label">
                        {t('receiptDetail.lastUpdateDate')}
                      </span>
                      <span className="receipt-detail__value">
                        {formatDateFull(receiptDetail?.updatedAt)}
                      </span>
                    </div>

                    <div className="receipt-detail__row">
                      <span className="receipt-detail__label">Dostava</span>
                      <span className="receipt-detail__value">
                        {formattedPrice(receiptDetail?.shippingPrice)}
                      </span>
                    </div>

                    {receiptDetail?.receiptCopyNumber && (
                      <div className="receipt-detail__row">
                        <span className="receipt-detail__label">
                          {t('receiptDetail.receiptCopyNumber')}
                        </span>
                        <span className="receipt-detail__value receipt-detail__mono">
                          {receiptDetail?.receiptCopyNumber}
                        </span>
                      </div>
                    )}

                    {receiptDetail?.dateOnReceiptCopy && (
                      <div className="receipt-detail__row">
                        <span className="receipt-detail__label">
                          {t('receiptDetail.dateOnReceiptCopy')}
                        </span>
                        <span className="receipt-detail__value">
                          {formatDateFull(receiptDetail?.dateOnReceiptCopy)}
                        </span>
                      </div>
                    )}

                    <div className="receipt-detail__row">
                      <span className="receipt-detail__label">
                        {t('receiptDetail.creationDate')}
                      </span>
                      <span className="receipt-detail__value">
                        {formatDateFull(receiptDetail?.createdAt)}
                      </span>
                    </div>

                    <div className="receipt-detail__row">
                      <span className="receipt-detail__label">Ukupan iznos</span>
                      <span className="receipt-detail__value receipt-detail__value--strong">
                        {formattedPrice(
                          (receiptDetail?.totalPrice || 0) + (receiptDetail?.shippingPrice || 0)
                        )}
                      </span>
                    </div>
                  </div>

                  {receiptDetail?.rejectReason && (
                    <div className="receipt-detail__reject">
                      <div className="receipt-detail__rejectLabel">
                        {t('receiptDetail.rejectReason')}
                      </div>
                      <div className="receipt-detail__rejectValue">
                        {receiptDetail?.rejectReason}
                      </div>
                    </div>
                  )}

                  <div className="receipt-table">
                    <div className="receipt-table__title">Stavke kupovine</div>

                    <div className="receipt-table__wrap">
                      <table className="receipt-table__table">
                        <thead>
                          <tr>
                            <th>{t('receiptDetail.product')}</th>
                            <th className="text-end">Cena</th>
                            <th className="text-center">Količina</th>
                            <th className="text-center">Veličina</th>
                            <th className="text-center">Nivo mlevenja</th>
                            <th className="text-center">Prženje</th>
                          </tr>
                        </thead>
                        <tbody>
                          {receiptDetail?.items?.map((item, key) => (
                            <tr key={key}>
                              <td className="receipt-table__product">{item?.name}</td>
                              <td className="text-end">{formattedPrice(item?.price)}</td>
                              <td className="text-center">{item?.quantity}</td>
                              <td className="text-center">
                                {(item?.size != 'DEFAULT' && formatSizeLabel(item?.size)) || '-'}
                              </td>
                              <td className="text-center">
                                {(item?.productType == 'COFFEE' &&
                                  formatCoffeeType(item?.coffeeType)) ||
                                  '-'}
                              </td>
                              <td className="text-center">
                                {formatFryingType(item?.frying) || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default ReceiptDetail;
